import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import {
    ArrowLeft,
    ArrowRight,
    Hash,
    FileDigit,
    Smartphone,
    Mail,
    CheckCircle2,
    Loader2,
    AlertCircle,
    X,
    ShieldCheck,
    FileText
} from 'lucide-react';

export default function Signup() {
    const navigate = useNavigate();
    const currentYear = new Date().getFullYear();

    const [formData, setFormData] = useState({
        clientNumber: '',
        rut: '',
        phone: '',
        email: '',
        agreedPolicies: false,
        agreedTerms: false,
        run: ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [apiError, setApiError] = useState('');

    const [showPoliciesModal, setShowPoliciesModal] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);

    const validateRutDigit = (rutCompleto) => {
        if (!rutCompleto.includes('-')) return false;
        const [body, dv] = rutCompleto.split('-');
        const cleanBody = body.replace(/\./g, '');
        if (cleanBody.length < 7) return false;

        let suma = 0;
        let multiplo = 2;
        for (let i = 1; i <= cleanBody.length; i++) {
            const index = multiplo * cleanBody.charAt(cleanBody.length - i);
            suma = suma + index;
            if (multiplo < 7) { multiplo = multiplo + 1; } else { multiplo = 2; }
        }
        const dvEsperado = 11 - (suma % 11);
        let dvFinal = (dvEsperado === 11) ? "0" : (dvEsperado === 10) ? "K" : dvEsperado.toString();
        return dv.toUpperCase() === dvFinal;
    };

    const formatRut = (value) => {
        let cleanValue = value.replace(/[^0-9kK]/g, '');
        if (cleanValue.length > 9) cleanValue = cleanValue.slice(0, 9);
        const body = cleanValue.slice(0, -1);
        const dv = cleanValue.slice(-1).toUpperCase();

        if (cleanValue.length > 1) {
            let formattedBody = "";
            for (let i = body.length - 1, j = 1; i >= 0; i--, j++) {
                formattedBody = body.charAt(i) + formattedBody;
                if (j % 3 === 0 && i !== 0) formattedBody = "." + formattedBody;
            }
            return `${formattedBody}-${dv}`;
        }
        return cleanValue;
    };

    const validateForm = () => {
        let newErrors = {};
        if (!formData.clientNumber) {
            newErrors.clientNumber = "El número de cliente es obligatorio";
        }
        if (!formData.rut || !validateRutDigit(formData.rut)) {
            newErrors.rut = "El RUT ingresado no es válido";
        }
        const phoneClean = formData.phone.replace(/\s/g, '');
        if (!phoneClean || phoneClean.length !== 9 || !phoneClean.startsWith('9')) {
            newErrors.phone = "El celular debe tener 9 dígitos y comenzar con 9";
        }
        if (formData.email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                newErrors.email = "Ingresa un correo electrónico válido";
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let nextValue = type === 'checkbox' ? checked : value;
        setApiError('');

        if (name === 'clientNumber') {
            nextValue = value.replace(/[^0-9]/g, '');
        }
        else if (name === 'rut') {
            nextValue = formatRut(value);
        }
        else if (name === 'phone') {
            const numbersOnly = value.replace(/[^0-9]/g, '');
            if (numbersOnly.length > 0 && !numbersOnly.startsWith('9')) {
                nextValue = '9' + numbersOnly;
            } else {
                nextValue = numbersOnly;
            }
            if (nextValue.length > 9) nextValue = nextValue.slice(0, 9);
        }

        setFormData(prev => ({ ...prev, [name]: nextValue }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const handlePhoneFocus = () => {
        if (!formData.phone) {
            setFormData(prev => ({ ...prev, phone: '9' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (validateForm()) {
            setIsSubmitting(true);
            setApiError('');

            const cleanRut = formData.rut.replace(/\./g, '');
            const [rutBody, rutDv] = cleanRut.split('-');

            const payload = {
                rut: parseInt(rutBody),
                dv: rutDv,
                nro_cliente: parseInt(formData.clientNumber),
                eid: config.aprEid,
                phone: `+56${formData.phone}`,
                email: formData.email || "",
                run: formData.run
            };

            try {
                const response = await fetch(`${config.apiBaseUrl}/api/sucursal-virtual/client-signup`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                const data = await response.json();

                // Los rechazos (celular_no_existe, rut_no_existe, cliente ajeno) traen
                // status: false y message; se muestra el message tal cual.
                if (response.ok && data.status !== false) {
                    navigate('/verification', { state: { phone: payload.phone, isSignup: true } });
                } else {
                    setApiError(data.message || 'Error al procesar el registro.');
                }
            } catch (err) {
                setApiError('No pudimos conectar con el servidor.');
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const InfoModal = ({ isOpen, onClose, title, icon: Icon, children }) => {
        if (!isOpen) return null;
        return (
            <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300" onClick={onClose}></div>
                <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 flex flex-col max-h-[80vh]">
                    <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-sky-100 text-sky-600 rounded-xl">
                                <Icon size={24} />
                            </div>
                            <h3 className="text-xl font-bold text-slate-800">{title}</h3>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors text-slate-400"><X size={20} /></button>
                    </div>
                    <div className="p-8 overflow-y-auto text-slate-600 text-sm leading-relaxed space-y-4">
                        {children}
                    </div>
                    <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end">
                        <button onClick={onClose} className="px-6 py-2 bg-sky-600 text-white rounded-xl font-bold hover:bg-sky-700 transition-colors">Entendido</button>
                    </div>
                </div>
            </div>
        );
    };

    const isFormReady =
        formData.clientNumber.trim() !== '' &&
        formData.rut.trim().length >= 9 &&
        formData.phone.trim().length === 9 &&
        formData.agreedPolicies &&
        formData.agreedTerms;

    const PoweredByFooter = ({ isDark = false, className = "" }) => (
        <div className={`flex flex-col items-center gap-1 ${className}`}>
            <span className={`text-[9px] font-bold uppercase tracking-widest text-center ${isDark ? 'text-sky-300/40' : 'text-slate-400'}`}>Powered by</span>
            <img className={isDark ? "image-invert-white" : ""} alt="Snap" src="/logo.snap.png" style={{height: '22px'}} />
            <div className={`text-[9px] text-center font-bold ${isDark ? 'text-sky-300/60' : 'text-slate-400'}`}>
                © {currentYear} {config.aprName}
            </div>
        </div>
    );

    return (
        <div className="min-h-screen w-full flex bg-slate-50 font-sans text-slate-600 relative overflow-hidden">
            <InfoModal
                isOpen={showPoliciesModal}
                onClose={() => setShowPoliciesModal(false)}
                title="Políticas de Uso"
                icon={ShieldCheck}
            >
                <p className="font-bold">Privacidad y Protección de Datos</p>
                <p>La información recolectada será utilizada exclusivamente para la gestión de su cuenta. No compartiremos sus datos con terceros sin su consentimiento.</p>
            </InfoModal>

            <InfoModal
                isOpen={showTermsModal}
                onClose={() => setShowTermsModal(false)}
                title="Términos y Condiciones"
                icon={FileText}
            >
                <p className="font-bold text-slate-800">Uso de la Sucursal Virtual</p>
                <p>El acceso es personal e intransferible. El usuario acepta recibir comunicaciones electrónicas relacionadas con su servicio de agua.</p>
            </InfoModal>

            <div className="hidden lg:flex lg:w-1/2 relative flex-col justify-between overflow-hidden bg-sky-600">
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1518837695005-2083093ee35b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1500&q=80"
                        alt="Agua de fondo"
                        className="w-full h-full object-cover opacity-50 mix-blend-overlay"
                    />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-sky-900/90 via-sky-800/40 to-transparent"></div>
                <div className="relative z-10 flex flex-col justify-between h-full p-10 xl:p-16 text-white w-full">
                    <div className="flex items-center gap-3">
                        <img className="image-invert-white" style={{height:'90px'}} src={config.aprLogo} alt={config.aprName} />
                    </div>
                    <div className="space-y-4 xl:space-y-6">
                        <h1 className="text-4xl xl:text-5xl font-bold leading-tight">Únete a la nueva forma de <span className="text-sky-300">gestionar tu agua.</span></h1>
                        <p className="text-base xl:text-lg text-sky-100 max-w-md leading-relaxed">Crea tu cuenta digital para recibir notificaciones de cortes y monitorear tu consumo histórico.</p>
                        <div className="mt-4 xl:mt-8 p-5 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20">
                            <div className="flex gap-4">
                                <div className="p-3 bg-emerald-500/20 rounded-full h-fit"><CheckCircle2 className="text-emerald-300" size={24} /></div>
                                <div><h3 className="font-bold text-lg mb-1">Registro Rápido</h3><p className="text-sm text-sky-100">Solo necesitas ser el titular de la cuenta o tener el número de servicio a mano.</p></div>
                            </div>
                        </div>
                    </div>

                    <PoweredByFooter isDark={true} className="mt-4" />
                </div>
            </div>

            <div className="w-full lg:w-1/2 flex items-center justify-center p-4 lg:p-8 xl:p-12 overflow-y-auto">
                <div className="w-full max-w-md flex flex-col min-h-full lg:min-h-0 py-4">
                    <div className="flex-1">
                        <button onClick={() => navigate('/login')} className="flex items-center gap-2 text-slate-400 hover:text-sky-600 mb-4 xl:mb-8 transition-colors font-medium text-sm"><ArrowLeft size={16} /> Volver al inicio</button>
                        <div className="lg:hidden flex justify-center mb-6"><img style={{height:'70px'}} src={config.aprLogo} alt={config.aprName} /></div>
                        <div className="text-center lg:text-left mb-4 xl:mb-8">
                            <h2 className="text-2xl xl:text-3xl font-bold text-slate-800">Crea tu Cuenta</h2>
                            <p className="mt-1 text-slate-500 text-sm">Completa tus datos para registrarte en la Sucursal Virtual.</p>
                        </div>

                        {apiError && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3 text-red-600 text-xs font-bold animate-pulse">
                                <AlertCircle size={16} className="shrink-0" /> {apiError}
                            </div>
                        )}

                        <form className="space-y-3 xl:space-y-4" onSubmit={handleSubmit}>
                            <div className="hidden" aria-hidden="true">
                                <input
                                    type="text"
                                    name="run"
                                    value={formData.run}
                                    onChange={handleChange}
                                    tabIndex="-1"
                                    autoComplete="off"
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-700 ml-1">N° Cliente / Servicio</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><Hash className="h-4 w-4 text-slate-400 group-focus-within:text-sky-500 transition-colors" /></div>
                                    <input type="text" name="clientNumber" value={formData.clientNumber} onChange={handleChange} maxLength={15} className={`block w-full pl-10 pr-4 py-2.5 xl:py-3 bg-white border rounded-xl text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-100 transition-all font-medium text-sm ${errors.clientNumber ? 'border-red-300' : 'border-slate-200 focus:border-sky-500'}`} placeholder="Ej: 123456" />
                                </div>
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-700 ml-1">RUT del Titular</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><FileDigit className="h-4 w-4 text-slate-400 group-focus-within:text-sky-500 transition-colors" /></div>
                                    <input type="text" name="rut" maxLength={12} value={formData.rut} onChange={handleChange} className={`block w-full pl-10 pr-4 py-2.5 xl:py-3 bg-white border rounded-xl text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-100 transition-all font-medium text-sm ${errors.rut ? 'border-red-300' : 'border-slate-200 focus:border-sky-500'}`} placeholder="12.345.678-9" />
                                </div>
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-700 ml-1">Celular</label>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><Smartphone className="h-4 w-4 text-slate-400 group-focus-within:text-sky-500 transition-colors" /></div>
                                    <input type="text" name="phone" maxLength={9} value={formData.phone} onFocus={handlePhoneFocus} onChange={handleChange} className={`block w-full pl-10 pr-4 py-2.5 xl:py-3 bg-white border rounded-xl text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-100 transition-all font-medium text-sm ${errors.phone ? 'border-red-300' : 'border-slate-200 focus:border-sky-500'}`} placeholder="9 1234 5678" />
                                </div>
                            </div>

                            <div className="space-y-1">
                                <div className="flex justify-between items-center ml-1"><label className="text-xs font-bold text-slate-700">Correo Electrónico</label><span className="text-[10px] text-slate-400 font-medium">(Opcional)</span></div>
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"><Mail className="h-4 w-4 text-slate-400 group-focus-within:text-sky-500 transition-colors" /></div>
                                    <input type="email" name="email" value={formData.email} onChange={handleChange} className={`block w-full pl-10 pr-4 py-2.5 xl:py-3 bg-white border rounded-xl text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-100 transition-all font-medium text-sm ${errors.email ? 'border-red-300' : 'border-slate-200 focus:border-sky-500'}`} placeholder="ejemplo@correo.com" />
                                </div>
                            </div>

                            <div className="space-y-2 py-1 xl:py-2">
                                <label className="flex items-start gap-3 cursor-pointer group">
                                    <div className="relative flex items-center mt-0.5">
                                        <input
                                            type="checkbox"
                                            name="agreedPolicies"
                                            checked={formData.agreedPolicies}
                                            onChange={handleChange}
                                            className="peer h-4 w-4 cursor-pointer appearance-none rounded-md border-2 border-slate-200 transition-all checked:bg-sky-600 checked:border-sky-600"
                                        />
                                        <CheckCircle2 size={12} className="absolute left-0.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity" />
                                    </div>
                                    <span className="text-[10px] xl:text-xs text-slate-500 leading-normal">
                                        Acepto las <button type="button" onClick={() => setShowPoliciesModal(true)} className="text-sky-600 font-bold hover:underline">políticas de uso</button> y privacidad.
                                    </span>
                                </label>

                                <label className="flex items-start gap-3 cursor-pointer group">
                                    <div className="relative flex items-center mt-0.5">
                                        <input
                                            type="checkbox"
                                            name="agreedTerms"
                                            checked={formData.agreedTerms}
                                            onChange={handleChange}
                                            className="peer h-4 w-4 cursor-pointer appearance-none rounded-md border-2 border-slate-200 transition-all checked:bg-sky-600 checked:border-sky-600"
                                        />
                                        <CheckCircle2 size={12} className="absolute left-0.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity" />
                                    </div>
                                    <span className="text-[10px] xl:text-xs text-slate-500 leading-normal">
                                        He leído y acepto los <button type="button" onClick={() => setShowTermsModal(true)} className="text-sky-600 font-bold hover:underline">términos y condiciones</button>.
                                    </span>
                                </label>
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting || !isFormReady}
                                className={`w-full flex items-center justify-center gap-2 py-3 xl:py-3.5 px-4 border border-transparent rounded-xl text-sm font-bold text-white transition-all 
                                    ${(!isFormReady)
                                    ? 'bg-slate-300 cursor-not-allowed opacity-70'
                                    : 'bg-sky-600 hover:bg-sky-700 shadow-lg shadow-sky-200 hover:shadow-xl hover:-translate-y-0.5 active:scale-95'
                                }`}
                            >
                                {isSubmitting ? <Loader2 className="animate-spin" size={18} /> : <>Crear mi Cuenta <ArrowRight size={16} /></>}
                            </button>
                        </form>

                        <div className="pt-4 xl:pt-6 text-center">
                            <p className="text-xs xl:text-sm text-slate-500">¿Ya tienes una cuenta? <button type="button" onClick={() => navigate('/login')} className="font-bold text-sky-600 hover:text-sky-700 hover:underline">Ingresa aquí</button></p>
                        </div>
                    </div>

                    <div className="lg:hidden mt-auto pt-8 pb-24">
                        <PoweredByFooter isDark={false} />
                    </div>
                </div>
            </div>
        </div>
    );
}
